import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { paymentsService } from '../../../../services/paymentsService';
import { shipmentService } from '../../../../services/shipmentService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faTimesCircle, faRedoAlt, faCircleNotch, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

function useQuery() {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

const PaystackCallback = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying | success | failed
  const [error, setError] = useState('');
  const [httpStatus, setHttpStatus] = useState(null);
  const [gatewayStatus, setGatewayStatus] = useState('');
  const [details, setDetails] = useState({ paidAmount: null, reference: null, shipmentId: null });
  // Paystack may send either `reference` or legacy `trxref`.
  const rawRef = query.get('reference') || query.get('trxref');
  // Handle edge case where reference contains a trailing slash from the router
  const reference = rawRef ? rawRef.replace(/\/$/, '') : null;

  const performVerify = useCallback(async () => {
    if (!reference) {
      setStatus('failed');
      setError('Missing payment reference in callback URL');
      setHttpStatus(400);
      setGatewayStatus('missing_reference');
      return;
    }
    setStatus('verifying');
    setError('');
    setHttpStatus(null);
    setGatewayStatus('');
    setDetails({ paidAmount: null, reference, shipmentId: null });
    try {
      const result = await paymentsService.verify(reference);
      const success = Boolean(result?.success);
      const gStatus = result?.status || '';
      setGatewayStatus(gStatus);
      setHttpStatus(200);
      setDetails({
        paidAmount: typeof result?.paidAmount === 'number' ? result.paidAmount : null,
        reference: result?.reference || reference,
        shipmentId: result?.shipment?._id || result?.shipment?.id || null,
        bundleId: result?.bundle?._id || result?.bundle?.id || null,
        bundleName: result?.bundle?.bundleName || null
      });
      if (success && gStatus === 'success') {
        // Attempt to force-update payment status for the logged-in user (optional)
        try {
          const token = localStorage.getItem('token');
          const sid = result?.shipment?._id || result?.shipment?.id;
          if (token && sid) {
            await shipmentService.updatePaymentStatus(sid, 'Paid', token);
          }
        } catch (updateErr) {
          // Non-blocking; verification already updated server-side
          console.warn('Client-side payment status update failed:', updateErr?.message || updateErr);
        }
        setStatus('success');
        if (result?.bundle) {
          setTimeout(() => navigate('/dashboard/user/bundles'), 1500);
        } else {
          setTimeout(() => navigate('/dashboard/user/shipments'), 1500);
        }
      } else {
        setStatus('failed');
        setError('Payment verification returned a non-success status.');
      }
    } catch (err) {
      const msg = err?.response?.data?.msg || err?.message || 'Failed to verify payment';
      const code = err?.response?.status || null;
      setError(msg);
      setHttpStatus(code);
      setGatewayStatus('error');
      setStatus('failed');
    }
  }, [reference, navigate]);

  useEffect(() => {
    performVerify();
  }, [performVerify]);

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="flex items-center mb-4">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mr-2" />
          <h1 className="text-2xl font-bold">Payment Confirmation</h1>
        </div>

        {status === 'verifying' && (
          <div className="flex items-center text-gray-700">
            <FontAwesomeIcon icon={faCircleNotch} className="animate-spin mr-3 text-gray-600 text-xl" />
            <span>Verifying payment… Please wait.</span>
          </div>
        )}

        {status === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mr-2 text-xl" />
              <p className="text-green-700 font-medium">Payment successful! Redirecting to {details.bundleId ? 'your bundles' : 'your shipments'}…</p>
            </div>
            <div className="mt-3 text-sm text-green-800">
              <div>Gateway status: <span className="font-semibold">{gatewayStatus || 'success'}</span></div>
              {typeof details.paidAmount === 'number' && (
                <div>Amount paid: <span className="font-semibold">₦{details.paidAmount}</span></div>
              )}
              {details.shipmentId && (
                <div>Updated shipment: <span className="font-semibold">{details.shipmentId}</span></div>
              )}
              {details.bundleName && (
                <div>Activated Bundle: <span className="font-semibold">{details.bundleName}</span></div>
              )}
            </div>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex items-center">
              <FontAwesomeIcon icon={faTimesCircle} className="text-red-600 mr-2 text-xl" />
              <p className="text-red-700 font-medium">Payment verification failed.</p>
            </div>
            <div className="mt-3 text-sm text-red-800 space-y-1">
              {gatewayStatus && (
                <div>Gateway status: <span className="font-semibold">{gatewayStatus}</span></div>
              )}
              {httpStatus && (
                <div>HTTP status: <span className="font-semibold">{httpStatus}</span></div>
              )}
              {error && (
                <div>Message: <span className="font-semibold">{error}</span></div>
              )}
            </div>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 flex items-center"
                onClick={performVerify}
              >
                <FontAwesomeIcon icon={faRedoAlt} className="mr-2" /> Retry verification
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-50"
                onClick={() => navigate('/dashboard/user/shipments')}
              >
                Back to Shipments
              </button>
            </div>
          </div>
        )}

        <div className="mt-6 text-xs text-gray-500">
          <div>Reference: {reference || '—'}</div>
        </div>
      </div>
    </div>
  );
};

export default PaystackCallback;